// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Clatter — "what you missed". Principle #9 asks that where a game has a knowable better answer, the
// summary says what everyone passed up, because that one screen is where players compare themselves.
// A roll-and-write has exactly that: every decision was a choice between legal placements whose
// immediate value is computable, so the biggest gap between what you played and the best thing
// available is a real, honest number rather than a hindsight opinion.
//
// It is deliberately IMMEDIATE value only — no lookahead, no bot judgement. "You could have had six
// more points right then" is a fact; "the bot would have played elsewhere" is an argument.

import {
  PH_ACTIVE,
  T_NONE,
  TRACK_NAME,
  aDie,
  aSeat,
  aShim,
  aSlot,
  aTrack,
  canonical,
  cloneSheet,
  createState,
  isOver,
  legalActions,
  place,
  score,
  shimDelta,
  step,
} from './rules';
import { modeOf } from './modes';

export interface Regret {
  /** Points left on the table by the single worst decision. */
  points: number;
  /** The face that was being placed. */
  face: number;
  /** Where it went, and where it could have gone. */
  played: string;
  better: string;
  turn: number;
}

/** The immediate points an action is worth to its own sheet, cascade included. */
function immediate(sheetScore: number, s: ReturnType<typeof createState>, seat: number, action: number): number {
  const track = aTrack(action);
  if (track === T_NONE) return 0;
  const dice = s.phase === PH_ACTIVE ? s.pool : s.spill;
  const face = dice[aDie(action)] + shimDelta(aShim(action));
  const after = cloneSheet(s.sheets[seat]);
  place(after, seat, track, face, aSlot(action), []);
  if (aShim(action) !== 0) after.shims--;
  return score(after, s.mode).total - sheetScore;
}

const label = (action: number): string => (aTrack(action) === T_NONE ? 'nothing' : TRACK_NAME[aTrack(action)]);

/** The single biggest thing `seat` left on the table. Null when they played it clean. */
export function worstMiss(modeId: string, seed: number, n: number, log: number[], seat: number): Regret | null {
  const s = createState(modeId, seed, n);
  let worst: Regret | null = null;
  for (const a of canonical(log)) {
    if (isOver(s)) break;
    if (aSeat(a) === seat) {
      const before = score(s.sheets[seat], s.mode).total;
      const chosen = immediate(before, s, seat, a);
      let best = chosen;
      let bestAction = a;
      for (const alt of legalActions(s, seat)) {
        const v = immediate(before, s, seat, alt);
        if (v > best) {
          best = v;
          bestAction = alt;
        }
      }
      const gap = best - chosen;
      if (gap > 0 && (worst === null || gap > worst.points)) {
        const dice = s.phase === PH_ACTIVE ? s.pool : s.spill;
        worst = {
          points: gap,
          face: dice[aDie(bestAction)] ?? 0,
          played: label(a),
          better: label(bestAction),
          turn: s.turn + 1,
        };
      }
    }
    if (!step(s, a)) break;
  }
  return worst;
}

/**
 * One line for the results screen. Deliberately generous in tone: a roll-and-write decision is often
 * a correct long-term sacrifice, so this says what was available, not that the player was wrong.
 */
export function missedNote(modeId: string, seed: number, n: number, log: number[], seat: number): string {
  const r = worstMiss(modeId, seed, n, log, seat);
  if (!r || r.points < 3) return '';
  if (r.played === r.better) return '';
  return `Biggest fork: on turn ${r.turn} that ${r.face} was worth ${r.points} more in the ${r.better} than where it went.`;
}

/** The par for a sheet: what the best immediate choice every turn would have scored. */
export function greedyPar(modeId: string, seed: number, n: number, log: number[], seat: number): number {
  const mode = modeOf(modeId);
  const s = createState(modeId, seed, n);
  let par = 0;
  for (const a of canonical(log)) {
    if (isOver(s)) break;
    if (aSeat(a) === seat) {
      const before = score(s.sheets[seat], mode).total;
      let best = 0;
      for (const alt of legalActions(s, seat)) best = Math.max(best, immediate(before, s, seat, alt));
      par += best;
    }
    if (!step(s, a)) break;
  }
  return par;
}
